import { createPathComponent } from '@react-leaflet/core';
import L from 'leaflet';

// Кастомный класс для отрисовки Polyline с текстом на canvas.
// Текст отрисовывается вдоль непрерывного участка линии с корректировкой угла так, чтобы текст читался слева направо.
// Поддерживаются стили текста (жирный, курсив), обводка (stroke) с толщиной и цветом,
// горизонтальное ужатие текста до тех пор, пока он не поместится, с ограничением maxTextCompressionPercent,
// а также отрисовка текста только при достижении определённого зума (minZoomForText).
class TextCanvasPolyline extends L.Polyline {
  _updatePath() {
    // Отрисовка линии стандартным способом (с учетом options.color и options.weight)
    L.Polyline.prototype._updatePath.call(this);

    const renderer = this._renderer;
    if (!renderer || !renderer._ctx) return;
    const ctx = renderer._ctx;
    const map = this._map;
    const latlngs = this.getLatLngs();
    if (!latlngs || latlngs.length < 2) return;

    // Переводим координаты в layer points (система координат canvas)
    const points = latlngs.map(latlng => map.latLngToLayerPoint(latlng));

    ctx.save();

    // Формируем строку шрифта.
    // Если textFont задан, он имеет приоритет, иначе собираем из отдельных опций.
    const textFontSize = this.options.textFontSize || "16px";
    const textFontFamily = this.options.textFontFamily || "sans-serif";
    const textFontStyle = this.options.textItalic ? "italic" : "normal";
    const textFontWeight = this.options.textBold ? "bold" : "normal";
    const fontString = this.options.textFont || `${textFontStyle} ${textFontWeight} ${textFontSize} ${textFontFamily}`.trim();
    ctx.font = fontString;
    ctx.fillStyle = this.options.textColor || "black";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Если включена обводка текста, задаем параметры stroke
    const doStroke = this.options.textStroke;
    if (doStroke) {
      ctx.lineWidth = this.options.textStrokeWidth || 1;
      ctx.strokeStyle = this.options.textStrokeColor || "white";
    }

    const text = this.options.text || "";
    if (!text) {
      ctx.restore();
      return;
    }

    // Если указан minZoomForText и текущий зум меньше порога – текст не отрисовывается.
    if (
      typeof this.options.minZoomForText === "number" &&
      map.getZoom() < this.options.minZoomForText
    ) {
      ctx.restore();
      return;
    }

    // Измеряем ширину каждой буквы (без масштабирования)
    const letterWidths = [];
    for (let i = 0; i < text.length; i++) {
      letterWidths.push(ctx.measureText(text[i]).width);
    }
    const totalTextWidth = letterWidths.reduce((sum, w) => sum + w, 0);

    // Группируем соседние отрезки, у которых разница углов меньше порога (15°)
    const groups = [];
    let groupPoints = [points[0]];
    let groupLength = 0;
    const angleThreshold = Math.PI / 12; // 15° в радианах
    for (let i = 0; i < points.length - 1; i++) {
      const currStart = points[i];
      const currEnd = points[i + 1];
      const dx = currEnd.x - currStart.x;
      const dy = currEnd.y - currStart.y;
      const segLength = Math.sqrt(dx * dx + dy * dy);
      const segAngle = Math.atan2(dy, dx);

      if (groupPoints.length > 1) {
        const lastIdx = groupPoints.length - 1;
        const prevStart = groupPoints[lastIdx - 1];
        const prevEnd = groupPoints[lastIdx];
        const prevDx = prevEnd.x - prevStart.x;
        const prevDy = prevEnd.y - prevStart.y;
        let prevAngle = Math.atan2(prevDy, prevDx);
        let angleDiff = Math.abs(segAngle - prevAngle);
        if (angleDiff > Math.PI) {
          angleDiff = Math.abs(angleDiff - 2 * Math.PI);
        }
        if (angleDiff > angleThreshold) {
          groups.push({ points: groupPoints.slice(), length: groupLength });
          groupPoints = [points[i]];
          groupLength = 0;
        }
      }
      groupPoints.push(points[i + 1]);
      groupLength += segLength;
    }
    groups.push({ points: groupPoints.slice(), length: groupLength });

    // Выбираем группу с максимальной длиной (для размещения текста)
    let chosenGroup = groups[0];
    for (let group of groups) {
      if (group.length > chosenGroup.length) {
        chosenGroup = group;
      }
    }
    // Вычисляем накопительные расстояния вдоль выбранной группы
    const groupPts = chosenGroup.points;
    const groupDistances = [0];
    for (let i = 1; i < groupPts.length; i++) {
      const dx = groupPts[i].x - groupPts[i - 1].x;
      const dy = groupPts[i].y - groupPts[i - 1].y;
      groupDistances.push(groupDistances[i - 1] + Math.sqrt(dx * dx + dy * dy));
    }
    const groupTotalLength = groupDistances[groupDistances.length - 1];

    // Коэффициент масштабирования по горизонтали (ужатие)
    const scaleFactor = Math.min(1, groupTotalLength / totalTextWidth);

    // Если задан maxTextCompressionPercent, проверяем, не превышает ли ужатие допустимое значение.
    // Например, если maxTextCompressionPercent = 50, то scaleFactor не должно быть меньше 0.5.
    if (typeof this.options.maxTextCompressionPercent === "number") {
      const minAllowedScale = this.options.maxTextCompressionPercent / 100;
      if (scaleFactor < minAllowedScale) {
        ctx.restore();
        return;
      }
    }

    // Вычисляем эффективные (с ужатием) ширины букв
    const effectiveLetterWidths = letterWidths.map(w => w * scaleFactor);
    const effectiveTotalTextWidth = totalTextWidth * scaleFactor;

    // Центрируем текст вдоль выбранной группы
    const offsetDistance = (groupTotalLength - effectiveTotalTextWidth) / 2;
    let currentDistance = offsetDistance;

    // Определяем вертикальное смещение относительно линии.
    // Если задан числовой textOffset – он имеет приоритет,
    // иначе если задан textPosition ("top", "center", "bottom"):
    // "top" => -10, "bottom" => 10, "center" => 0.
    const offsetOption =
      typeof this.options.textOffset === "number"
        ? this.options.textOffset
        : this.options.textPosition === "top"
          ? -10
          : this.options.textPosition === "bottom"
            ? 10
            : 0;

    // Определяем общее направление выбранной группы
    const groupStart = chosenGroup.points[0];
    const groupEnd = chosenGroup.points[chosenGroup.points.length - 1];
    const isGroupRTL = groupEnd.x < groupStart.x; // Направление справа-налево

 


    // Отрисовываем каждую букву вдоль выбранной группы
    for (let i = 0; i < text.length; i++) {
      const letter = isGroupRTL ? [...text].reverse().join("")[i] : text[i];
      const effLetterWidth = effectiveLetterWidths[i];
      // Позиция центра буквы вдоль линии (с учетом ужатых размеров)
      const letterCenterDistance = currentDistance + effLetterWidth / 2;

      // Находим сегмент выбранной группы, где лежит данная позиция
      let segIndex = 0;
      while (
        segIndex < groupDistances.length - 1 &&
        groupDistances[segIndex + 1] < letterCenterDistance
      ) {
        segIndex++;
      }
      const segStart = groupPts[segIndex];
      const segEnd = groupPts[segIndex + 1];
      const segLength = groupDistances[segIndex + 1] - groupDistances[segIndex];
      const t = (letterCenterDistance - groupDistances[segIndex]) / segLength;

      
      const x = segStart.x + t * (segEnd.x - segStart.x);
      const y = segStart.y + t * (segEnd.y - segStart.y);

      // Вычисляем угол сегмента
      let angle = Math.atan2(segEnd.y - segStart.y, segEnd.x - segStart.x);

      // Корректируем угол, чтобы он всегда был в диапазоне [-90°, 90°]
      if (angle > Math.PI / 2) {
        angle -= Math.PI;
      } else if (angle < -Math.PI / 2) {
        angle += Math.PI;
      }
      // Вычисляем вектор нормали для вертикального смещения
      const normalX = Math.sin(angle);
      const normalY = -Math.cos(angle);
      const finalX = x + offsetOption * normalX;
      const finalY = y + offsetOption * normalY;

      ctx.save();
      ctx.translate(finalX, finalY);
      ctx.rotate(angle);
      // Применяем горизонтальное масштабирование (сжатие)
      ctx.scale(scaleFactor, 1);
      if (doStroke) {
        ctx.strokeText(letter, 0, 0);
      }
      ctx.fillText(letter, 0, 0);
      ctx.restore();

      currentDistance += effLetterWidth;
    }
    ctx.restore();
  }
}

const createTextPolyline = (props, context) => {
  const instance = new TextCanvasPolyline(props.positions, getOptions(props));

  return {
    instance,
    context: {
      ...context,
      overlayContainer: instance
    }
  };
};

const updateTextPolyline = (instance, props, prevProps) => {
  if (props.positions !== prevProps.positions) {
    instance.setLatLngs(props.positions);
  }

  if (props.color !== prevProps.color || props.weight !== prevProps.weight) {
    instance.setStyle({
      color: props.color,
      weight: props.weight
    });
  }

  // Обновляем параметры текста при изменении
  const textOptions = getTextOptions(props);
  const prevTextOptions = getTextOptions(prevProps);
  if (JSON.stringify(textOptions) !== JSON.stringify(prevTextOptions)) {
    instance.options = { ...instance.options, ...textOptions };
    instance.redraw();
  }
};

const getOptions = (props) => ({
  ...props,
  color: props.lineColor || props.color || "blue",
  weight: props.lineWeight || props.weight || 3,
  ...getTextOptions(props)
});

const getTextOptions = (props) => ({
  text: props.text,
  textColor: props.textColor || "black",
  textFont: props.textFont,
  textBold: props.textBold || false,
  textItalic: props.textItalic || false,
  textFontSize: props.textFontSize || "16px",
  textFontFamily: props.textFontFamily || "sans-serif",
  textStroke: props.textStroke || false,
  textStrokeWidth: props.textStrokeWidth || 1,
  textStrokeColor: props.textStrokeColor || "white",
  textOffset: typeof props.textOffset === "number" ? props.textOffset : undefined,
  textPosition: props.textPosition || "center",
  maxTextCompressionPercent: props.maxTextCompressionPercent,
  minZoomForText: props.minZoomForText,
});

const TextPolyline = createPathComponent(
  createTextPolyline,
  updateTextPolyline
);

export default TextPolyline;